import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Button, Modal, Switch, Tooltip, notification } from 'antd'
import * as Icons from '@ant-design/icons'
import produce from 'immer'
import _ from 'lodash'

import {
  AddNewItem,
  CreationDate,
  Form,
  FormSectionTitle,
  FormSectionWrapper,
  SectionProvider
} from 'admin/lib/Forms'
import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import {
  getLocaleFromCountry,
  isValidTimeZone
} from 'admin/lib/Functions/timeZoneHelper'
import { Flag } from 'admin/lib/Icons/Flag'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { Matchers } from 'admin/lib/Records'

import { DeliveryLineItem } from './DeliveryLineItem'
import { DeliveryData } from './DeliveryData'
import {
  AddMultipleLineItemsModal,
  DuplicateRecordControls,
  FormControls,
  LastModifiedBy,
  RecordHistory,
  focusFirstError,
  useLineItemsManagement,
  useExtractionValueClicked,
  AttachmentView,
  difference,
  TemplateNameLabel,
  AutoPublishFailureReason
} from 'admin/pages/RecordEdit/common'

import {
  isoDateFromFormDate,
  isoDateToFormDate,
  cleanDate
} from 'admin/lib/dates'
import { Guid } from 'admin/lib/guid'
import { useAppInsights } from 'admin/lib/AppInsights/appinsights'
import { assembleLinkForMatchedRecord } from 'admin/pages/RecordEdit/common/assembleLinkForMatchedRecord'
import {
  RAW,
  ENRICHED,
  ENRICHED_DISABLED_MESSAGE
} from 'admin/pages/RecordEdit_v2/common/constants'
import { LineItemSummary } from 'admin/pages/RecordEdit_v2/common/LineItemSummary'
import {
  LineItemWrapper,
  TitleBar,
  ShowRawWrapper,
  DataToggle,
  DataToggleItem
} from 'admin/pages/RecordEdit_v2/common/styledComponents'

const DEFAULT_PRODUCT = Object.freeze({
  id: null,
  name: '',
  height: '',
  heightUoM: 'Millimeter',
  width: '',
  widthUoM: 'Millimeter',
  length: '',
  lengthUoM: 'Millimeter',
  weight: '',
  weightUoM: 'Tonne',
  quantity: 1,
  productionTime: '',
  usedAtTime: '',
  calculatedVolume: '',
  givenVolume: '',
  givenVolumeUoM: 'CubicMeter',
  estimatedWeight: '',
  referenceProductId: Guid.Empty,
  givenCertifications: []
})

const DEFAULT_PRODUCT_IMPERIAL = Object.freeze({
  ...DEFAULT_PRODUCT,
  weightUoM: 'Pound',
  givenVolumeUoM: 'CubicYard'
})

const DEFAULT_RAW_PRODUCT = Object.freeze({
  id: null,
  name: '',
  height: '',
  width: '',
  length: '',
  weight: '',
  quantity: '',
  productionDate: '',
  productionTime: '',
  usedAtDate: '',
  usedAtTime: '',
  volume: '',
  certifications: ''
})

export function Delivery({
  record,
  submit,
  project,
  isImperial,
  userAccountMappings,
  showHistory,
  onHistoryCloseClick
}) {
  const [reloadProductsCount, setReloadProductsCount] = useState(0)
  const [showDebug, setShowDebug] = useState(false)
  const [lineItemSummaryOpen, setLineItemSummaryOpen] = useState(false)
  const [isAddMultipleLineItemsOpen, setIsAddMultipleLineItemsOpen] =
    useState(false)

  const [dataView, setDataView] = useState(RAW)

  if (sessionStorage.getItem('last-data-view')) {
    setDataView(sessionStorage.getItem('last-data-view'))
    sessionStorage.removeItem('last-data-view')
  }

  const [showRaw, setShowRaw] = useState(false)
  const [enrichedDisabled, setEnrichedDisabled] = useState(false)

  const form = useFormContext()
  const appInsights = useAppInsights()
  const { moduleName, projectId, recordId, accountId } = useParams()
  const { reset, setValue } = form

  const projectCountry = project.country
  const projectTimeZone = project.timeZone ?? 'Europe/London'
  const projectLocale = getLocaleFromCountry(projectCountry)
  const MULTIATTACHMENT_WARNING_MESSAGE =
    'Multiple images uploaded with this record'

  const {
    methods,
    rawMethods,
    lineItemDuplicateHandlers,
    lineItemRemoveHandlers,
    handleAppendLineItem,
    handleAppendLineItemNoDebounce
  } = useLineItemsManagement(
    form,
    'enriched.products',
    'raw.products',
    isImperial ? DEFAULT_PRODUCT_IMPERIAL : DEFAULT_PRODUCT,
    DEFAULT_RAW_PRODUCT
  )

  const hasOneOrMoreLineItemWithProductGroupsContainingConcrete = () => {
    if (Array.isArray(methods.fields)) {
      return methods.fields.some(
        item =>
          item.productGroups &&
          Array.isArray(item.productGroups) &&
          item.productGroups.some(pg =>
            pg.name.toLowerCase().startsWith('concrete')
          )
      )
    }
    return false
  }

  const focusOnNewItem = () => {
    // focus on new element when ready
    const waitForInterval = setInterval(() => {
      const element = document.querySelector(
        `textarea[name^="uniquePrefix[${methods.fields.length}].raw_name"`
      )
      if (element !== null) {
        clearInterval(waitForInterval)
        element.focus()
        const form = document.getElementsByTagName('form')[0]
        form.scrollTo({
          top: form.scrollHeight - 850,
          behavior: 'smooth'
        })
      }
    }, 500)
  }

  const focusOnFirstNewItem = numOfNewItems => {
    // focus on first new element when ready
    const waitForInterval = setInterval(() => {
      const element = document.querySelector(
        `textarea[name^="uniquePrefix[${methods.fields.length}].raw_name"`
      )
      if (element !== null) {
        clearInterval(waitForInterval)
        element.closest('details').scrollIntoView({ behavior: 'smooth' })
        setTimeout(() => element.focus(), 500)
      }
    }, 500)
  }

  const openAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(true)
  }

  const closeAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(false)
  }

  const addNewLineItem = e => {
    handleAppendLineItem()
    focusOnNewItem()
  }

  const addMultipleNewLineItems = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        handleAppendLineItemNoDebounce()
      }
      focusOnFirstNewItem(numOfNewItems)
    }
  }

  const handleAddNewLineItem = HandleSingleAndDoubleClick(
    addNewLineItem,
    openAddNewItemsModal
  )

  const duplicateLineItem = item => {
    lineItemDuplicateHandlers(item)
    focusOnNewItem()
  }

  const duplicateLineItemMultiple = (numOfNewItems, originalItem) => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        lineItemDuplicateHandlers(originalItem)
      }
      focusOnFirstNewItem(numOfNewItems)
    }
  }

  const extractionValueClicked = useExtractionValueClicked(setValue)

  const matchedRecordLink = useMemo(() => {
    return assembleLinkForMatchedRecord(
      record.matchedRecord,
      'Delivery',
      userAccountMappings
    )
  }, [record.matchedRecord, userAccountMappings])

  useEffect(() => {
    reset(transformRecordToFormData(record, projectTimeZone))
  }, [projectTimeZone, record, reset])

  const toggleDebug = e => {
    if (e.detail === 3) {
      setShowDebug(s => !s)
    }
  }

  const handleToggleDataView = () => {
    if (dataView === RAW) {
      if (!enrichedDisabled) {
        performance.mark('raw-enriched-tab-switch', {
          detail: { context: 'shortcut', destination: ENRICHED }
        })
        setDataView(ENRICHED)
      } else {
        notification.warning({
          message: ENRICHED_DISABLED_MESSAGE,
          placement: 'topLeft'
        })
      }
    } else {
      performance.mark('raw-enriched-tab-switch', {
        detail: { context: 'shortcut', destination: RAW }
      })
      setDataView(RAW)
    }
  }

  const handlToggleShowRaw = () => {
    setShowRaw(prev => !prev)
  }

  const handleSubmit = form.handleSubmit(
    formRecord => {
      submit(
        transformFormDataToRecord(record, formRecord, appInsights, {
          projectId,
          recordId,
          projectTimeZone
        }),
        dataView
      )
      form.reset({}, { keepValues: true })
    },
    () => focusFirstError(form.errors, {})
  )

  const submitAddressCheck = event => {
    event.preventDefault()
    const formValues = form.getValues()
    if (
      dataView === ENRICHED &&
      (formValues.status === 'Published' ||
        formValues.status === 'AutoPublished') &&
      ((formValues.enriched.deliveryAddress &&
        !formValues.enriched.deliveryAddressKnown) ||
        (formValues.enriched.givenDispatchAddress &&
          !formValues.enriched.dispatchAddressKnown))
    ) {
      Modal.confirm({
        title: '1 or more Known Addresses missing.',
        content: (
          <>
            <p>This record will be published without mileage calculation.</p>
            <p> Are you sure you want to publish this record?</p>
          </>
        ),
        okText: 'Save enriched',
        onOk: handleSubmit
      })
    } else {
      handleSubmit()
    }
  }

  const openAllProducts = () => {
    document.querySelectorAll('.LineItem').forEach((item, index) => {
      sessionStorage.setItem(`${recordId}-${index}-open`, true)
    })
    setReloadProductsCount(prev => prev + 1)
  }

  const closeAllProducts = () => {
    document.querySelectorAll('.LineItem').forEach((item, index) => {
      sessionStorage.setItem(`${recordId}-${index}-open`, false)
    })
    setReloadProductsCount(prev => prev + 1)
  }

  useGlobalShortcut(Matchers.form.toggleDataView, handleToggleDataView)
  useGlobalShortcut(Matchers.form.toggleShowRaw, handlToggleShowRaw)
  useGlobalShortcut(Matchers.lineItems.openLineItemSummary, () =>
    setLineItemSummaryOpen(true)
  )
  useGlobalShortcut(Matchers.lineItems.minimizeProduct, () => {
    const parentDetails = document.activeElement?.closest('details')
    if (parentDetails) {
      parentDetails.querySelector('.productCollapseIcon')?.click()

      // Move focus to first element of next open <details>
      // This is to ensure that focus is moved to the next open product
      let element = parentDetails.parentElement

      while (element) {
        element = element.nextElementSibling
        const nextDetails = element?.querySelector('details[open]')
        if (nextDetails) {
          const firstInput = nextDetails.querySelector('textarea')
          if (firstInput) {
            firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
            firstInput.focus()
            break
          }
        }
      }
    }
  })

  return (
    <SectionProvider>
      <Form id="form-panel" onSubmit={submitAddressCheck}>
        {record.recordSimilarityDetails && (
          <DuplicateRecordControls
            recordSimilarityDetails={record.recordSimilarityDetails}
            recordStatus={record.status}
          />
        )}

        <TemplateNameLabel
          templateName={record.templateName}
          estimatedAccuracy={record.estimatedAccuracy}
          link={matchedRecordLink}
        />
        <AutoPublishFailureReason
          autoPublishFailureReasons={record.autoPublishFailureReasons}
          recordStatus={record.status}
        />
        {record.attachmentPages > 1 && (
          <p className="warning">{MULTIATTACHMENT_WARNING_MESSAGE}</p>
        )}
        <FormControls dataView={dataView} />
        <LastModifiedBy
          modifier={record.modifier}
          modifiedById={record.modifiedBy}
        />
        {record.migrationSource !== null && (
          <p>
            Migrated from recordId: {record.migrationSource.recordId}
            <br />
            Migration Reason: {record.migrationSource.migrationReason}
          </p>
        )}
        <CreationDate style={{ margin: '0 0 8px' }}>
          <>
            <div style={{ fontWeight: '600' }} onClick={toggleDebug}>
              Creation Date
            </div>
            <div style={{ lineHeight: '1.2', margin: '4px 0' }}>
              {`${
                projectTimeZone &&
                isValidTimeZone(projectTimeZone) &&
                Intl.DateTimeFormat().resolvedOptions().timeZone !==
                  projectTimeZone
                  ? 'Local: '
                  : ''
              }${new Date(record.createdAt).toLocaleString([], {
                timeZoneName: 'short',
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
              })}`}
              {
                // If Project Locale differs from local Locale
                // Or project TimeZone differs from local TimeZone
                (projectLocale !==
                  Intl.DateTimeFormat().resolvedOptions().locale ||
                  (projectTimeZone &&
                    isValidTimeZone(projectTimeZone) &&
                    Intl.DateTimeFormat().resolvedOptions().timeZone !==
                      projectTimeZone)) && (
                  <>
                    <br />
                    Project:{' '}
                    {new Date(record.createdAt).toLocaleString(projectLocale, {
                      timeZone: projectTimeZone,
                      timeZoneName: 'short',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: 'numeric',
                      minute: 'numeric'
                    })}
                  </>
                )
              }
            </div>
          </>
        </CreationDate>
        {showDebug && (
          <div
            style={{ marginBottom: '5px', fontSize: 'smaller' }}
            tabindex="-1"
          >
            <dl>
              <dt>Journey distance (mi)</dt>
              <dd>{record.enriched.journey?.distanceMiles}</dd>
            </dl>
            <dl>
              <dt>
                Laden journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.enriched.journey?.ladenEmissionsKgCO2e}</dd>
            </dl>
            <dl>
              <dt>
                Unladen journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.enriched.journey?.unladenEmissionsKgCO2e}</dd>
            </dl>
            {record.enriched.products.map((p, i) => (
              <dl>
                <dt>
                  Product ({i + 1}) Embodied Carbon (kg CO<sub>2</sub>e)
                </dt>
                <dd>{p.embodiedCarbonKgCO2e}</dd>
              </dl>
            ))}
          </div>
        )}
        <DeliveryData
          createdAt={record.createdAt}
          projectName={project.name}
          projectCountry={projectCountry}
          projectTimeZone={projectTimeZone}
          projectId={projectId}
          isImperial={isImperial}
          hasOneOrMoreLineItemWithProductGroupsContainingConcrete={hasOneOrMoreLineItemWithProductGroupsContainingConcrete()}
          dataView={dataView}
          showRaw={showRaw}
          enrichedDisabled={enrichedDisabled}
          setDataView={value => {
            setDataView(value)
          }}
          setShowRaw={value => {
            setShowRaw(value)
          }}
          setEnrichedDisabled={value => {
            setEnrichedDisabled(value)
          }}
        />

        <LineItemWrapper className="LineItemWrapper">
          <FormSectionWrapper
            style={{
              flexDirection: 'column',
              width: '100%',
              alignItems: 'flex-end'
            }}
          >
            <TitleBar>
              <FormSectionTitle level={5}>
                <Flag country={projectCountry} /> Products (
                {
                  methods.fields.filter(
                    item => !item.isDeleted && !item.isRemoved
                  ).length
                }
                )
              </FormSectionTitle>
              {dataView === ENRICHED && (
                <ShowRawWrapper>
                  <Switch
                    checked={showRaw}
                    onChange={() => setShowRaw(prev => !prev)}
                    size="small"
                  />
                  <div style={{ marginTop: '1px' }}>Show Raw</div>
                </ShowRawWrapper>
              )}
              <DataToggle>
                <DataToggleItem
                  className={dataView === RAW ? 'active' : ''}
                  onClick={() => {
                    performance.mark('raw-enriched-tab-switch', {
                      detail: { context: 'products', destination: RAW }
                    })
                    setDataView(RAW)
                  }}
                >
                  Raw
                </DataToggleItem>
                <DataToggleItem
                  className={
                    dataView === ENRICHED
                      ? 'active'
                      : enrichedDisabled
                        ? 'disabled'
                        : ''
                  }
                  onClick={() => {
                    if (!enrichedDisabled) {
                      performance.mark('raw-enriched-tab-switch', {
                        detail: { context: 'products', destination: ENRICHED }
                      })
                      setDataView(ENRICHED)
                    }
                  }}
                >
                  <Tooltip
                    title={enrichedDisabled ? ENRICHED_DISABLED_MESSAGE : ''}
                    placement="right"
                    mouseLeaveDelay={0.5}
                  >
                    Enriched
                  </Tooltip>
                </DataToggleItem>
              </DataToggle>
            </TitleBar>

            <TitleBar style={{ alignItems: 'baseline', margin: '0.25rem 0' }}>
              <div>
                <Tooltip title="Open all products">
                  <Icons.EyeOutlined
                    style={{ fontSize: '1.25rem', marginRight: '0.5rem' }}
                    onClick={openAllProducts}
                  />
                </Tooltip>
                <Tooltip title="Close all products">
                  <Icons.EyeInvisibleOutlined
                    style={{ fontSize: '1.25rem' }}
                    onClick={closeAllProducts}
                  />
                </Tooltip>
              </div>
              <Button onClick={() => setLineItemSummaryOpen(true)}>
                View Summary
              </Button>
              {lineItemSummaryOpen && methods.fields && rawMethods.fields && (
                <LineItemSummary
                  isOpen={lineItemSummaryOpen}
                  onClose={() => setLineItemSummaryOpen(false)}
                  items={methods.fields}
                  rawItems={rawMethods.fields}
                  projectTimeZone={projectTimeZone}
                  isImperial={isImperial}
                  setDataView={setDataView}
                  arrivalDate={record.enriched.deliveryArrivalDate}
                  exitDate={record.enriched.deliveryExitDate}
                  enrichedDisabled={enrichedDisabled}
                />
              )}
              {dataView === RAW && (
                <AddNewItem
                  onClick={handleAddNewLineItem}
                  style={{ margin: '0.5rem 0' }}
                >
                  Add new product
                </AddNewItem>
              )}
              {dataView === ENRICHED && (
                <p style={{ margin: '0.5rem 0', minWidth: '6.625rem' }}>
                  &nbsp;
                </p>
              )}
            </TitleBar>
          </FormSectionWrapper>
          {methods.fields.map((item, i) => (
            <DeliveryLineItem
              key={item.hookFormId}
              trigger={`${item.hookFormId}-${reloadProductsCount}`}
              index={i}
              item={item}
              rawItem={
                rawMethods.fields && rawMethods.fields[i]
                  ? rawMethods.fields[i]
                  : {}
              }
              onDuplicate={duplicateLineItem}
              onDuplicateMultiple={duplicateLineItemMultiple}
              onRemove={lineItemRemoveHandlers[i]}
              isImperial={isImperial}
              dataView={dataView}
              showRaw={showRaw}
              setEnrichedDisabled={value => {
                setEnrichedDisabled(value)
              }}
            />
          ))}

          {dataView === RAW && (
            <Button
              onClick={handleAddNewLineItem}
              block
              icon={<Icons.PlusCircleFilled />}
              type="dashed"
              htmlType="button"
            >
              Add new product
            </Button>
          )}
          <Button
            onClick={e => (document.getElementById('form-panel').scrollTop = 0)}
            style={{ marginTop: '1rem' }}
            className="backToTopButton"
          >
            Back to top
          </Button>
        </LineItemWrapper>
      </Form>

      <AttachmentView
        record={record}
        handleExtractionValueClicked={extractionValueClicked}
      />
      {record.history && (
        <RecordHistory
          changes={record.history.changes}
          moduleName={moduleName}
          isImperial={isImperial}
          projectLocale={projectLocale}
          projectTimeZone={projectTimeZone}
          lineItemCount={record.data.products.length}
          onCloseClick={onHistoryCloseClick}
          showHistory={showHistory}
          accountId={accountId}
          projectId={projectId}
          recordId={recordId}
        />
      )}

      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isAddMultipleLineItemsOpen}
        onClose={closeAddNewItemsModal}
        onSubmit={val => {
          addMultipleNewLineItems(val)
        }}
        duplicateLineItemName={null}
      />
    </SectionProvider>
  )
}

Delivery.propTypes = {
  record: PropTypes.object,
  submit: PropTypes.func.isRequired,
  userAccountMappings: PropTypes.array
}

//
// Transforms

function transformFormDataToRecord(
  record,
  formRecord,
  appInsights,
  { projectId, recordId, projectTimeZone }
) {
  const diffArray = difference(record, formRecord)
  diffArray.forEach(diff =>
    appInsights.trackEvent(
      { name: 'new-admin_update_field' },
      {
        status: formRecord.status,
        field: diff.field,
        new_value: diff.newValue ?? null,
        old_value: diff.oldValue ?? null,
        module: 'Delivery',
        project_id: projectId,
        record_id: recordId
      }
    )
  )
  appInsights.flush()

  return produce(_.merge({}, record, formRecord), draft => {
    draft.enriched.deliveryArrivalDate = isoDateFromFormDate(
      draft.enriched.deliveryArrivalDate,
      projectTimeZone
    )
    draft.enriched.deliveryExitDate = isoDateFromFormDate(
      draft.enriched.deliveryExitDate,
      projectTimeZone
    )
    draft.assignee = formRecord.assignee

    draft.enriched.products = formRecord.enriched.products
  })
}

function transformRecordToFormData(record, projectTimeZone) {
  return produce(record, draft => {
    draft.enriched.deliveryArrivalDate = cleanDate(
      draft.enriched.deliveryArrivalDate
    )
    draft.enriched.deliveryExitDate = cleanDate(draft.enriched.deliveryExitDate)
    draft.enriched.deliveryArrivalDate = isoDateToFormDate(
      draft.enriched.deliveryArrivalDate,
      projectTimeZone
    )
    draft.enriched.deliveryExitDate = isoDateToFormDate(
      draft.enriched.deliveryExitDate,
      projectTimeZone
    )

    for (const product of draft.enriched.products ?? []) {
      product.usedAtTime = cleanDate(product.usedAtTime)
      product.productionTime = cleanDate(product.productionTime)
      product.productionTime = isoDateToFormDate(
        product.productionTime,
        projectTimeZone
      )
      product.usedAtTime = isoDateToFormDate(
        product.usedAtTime,
        projectTimeZone
      )
    }
  })
}
